// mobile/pages/Collections/HlsPlayer.tsx

import React, { useEffect, useRef, useState } from 'react';
import Hls from 'hls.js';

export const HLSPlayer = ({
  src,
  poster,
  muted,
  active,
  videoId,
  style = {},
  onOrientation,
  onReady,
}: {
  src: string;
  poster?: string;
  muted: boolean;
  active: boolean;
  videoId: string;
  style?: React.CSSProperties;
  onOrientation?: (isLandscape: boolean) => void;
  onReady?: () => void;
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const pendingPlayRef = useRef(false);
  const hlsRef = useRef<Hls | null>(null);

  const [showControls, setShowControls] = useState(false);
  const [progress, setProgress] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(muted);
  const [userPaused, setUserPaused] = useState(false);

  const emitOrientation = (v: HTMLVideoElement) => {
    if (!v.videoWidth || !v.videoHeight) return;
    onOrientation?.(v.videoWidth > v.videoHeight);
  };

  const PlayIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="#bcb4b4">
      <path d="M7 5v14l11-7z" />
    </svg>
  );

  const PauseIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="#bcb4b4">
      <rect x="7.2" y="5" width="3.6" height="14" rx="1.2" />
      <rect x="13.2" y="5" width="3.6" height="14" rx="1.2" />
    </svg>
  );

  const SpeakerOnIcon = () => (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#bbb"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
      <path d="M15 9a4 4 0 0 1 0 6" />
      <path d="M18 7a7 7 0 0 1 0 10" />
    </svg>
  );

  const SpeakerOffIcon = () => (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="#bbb"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
      <line x1="23" y1="9" x2="17" y2="15" />
      <line x1="17" y1="9" x2="23" y2="15" />
    </svg>
  );

/* ---------------- HLS attach ---------------- */
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !src) return;

    let cancelled = false;

    if (video.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = src;
      video.load();
    } else if (Hls.isSupported()) {
      const hls = new Hls({
        startLevel: 0,
        enableWorker: true,
      });

      hlsRef.current = hls;
      hls.attachMedia(video);

      hls.on(Hls.Events.MEDIA_ATTACHED, () => {
        if (cancelled) return;
        hls.loadSource(src);
      });
    }

return () => {
  cancelled = true;

  try {
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }

    video.pause();
    video.removeAttribute('src');
    video.load();
  } catch {}
};
  }, [src]);


useEffect(() => {
  const handleOtherVideoPlay = (event: Event) => {
    const customEvent = event as CustomEvent<{ videoId: string }>;
    const playingId = customEvent.detail?.videoId;

    if (playingId === videoId) return;

    const v = videoRef.current;
    if (!v) return;

    pendingPlayRef.current = false;

    if (!v.paused) {
      v.pause();
    }
  };

  window.addEventListener("preview-video-play", handleOtherVideoPlay as EventListener);

  return () => {
    window.removeEventListener("preview-video-play", handleOtherVideoPlay as EventListener);
  };
}, [videoId]);



useEffect(() => {
  const v = videoRef.current;
  if (!v) return;

  if (!active) {
    pendingPlayRef.current = false;
    v.pause();
    return;
  }

  if (!userPaused) {
    pendingPlayRef.current = true;

    if (v.readyState >= 2) {
      v.play().catch(() => {});
    }
  }
}, [active, userPaused]);


  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = isMuted;
  }, [isMuted]);

  const onTimeUpdate = () => {
    const v = videoRef.current;
    if (!v || !v.duration) return;
    setProgress((v.currentTime / v.duration) * 100);
  };

  const handleScrub = (value: number) => {
    const v = videoRef.current;
    if (!v || !v.duration) return;

    v.currentTime = (v.duration * value) / 100;
    setProgress(value);
  };

  const handleVolume = (value: number) => {
    const v = videoRef.current;
    if (!v) return;

    v.volume = value;
    setVolume(value);
  };

  const formatTime = (sec: number) => {
    if (!sec || Number.isNaN(sec)) return '0:00';
    const m = Math.floor(sec / 60);
    const s = Math.floor(sec % 60)
      .toString()
      .padStart(2, '0');
    return `${m}:${s}`;
  };

const togglePlay = (e?: React.PointerEvent | React.MouseEvent) => {
  e?.stopPropagation();

  const v = videoRef.current;
  if (!v) return;

  if (!v.paused && !v.ended) {
    pendingPlayRef.current = false;
    v.pause();
    setUserPaused(true);
    return;
  }

  setUserPaused(false);
  pendingPlayRef.current = true;
  setShowControls(true);

  window.dispatchEvent(
    new CustomEvent("preview-video-play", {
      detail: { videoId },
    })
  );

  if (v.readyState >= 2) {
    v.play().catch(() => {});
  }
};


  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        overflow: 'hidden',
      }}
      onClick={() => setShowControls((prev) => !prev)}
    >
      <video
        ref={videoRef}
        poster={poster}
        muted={isMuted}
        playsInline
        preload="metadata"
        controls={false}
        onPointerDown={togglePlay}
        onLoadedMetadata={(e) => {
          emitOrientation(e.currentTarget);
          }}
        onLoadedData={(e) => {
          emitOrientation(e.currentTarget);
          onReady?.();
          }}
        onCanPlay={(e) => {
          emitOrientation(e.currentTarget);

          const v = videoRef.current;
            if (!v) return;
            if (!pendingPlayRef.current) return;
            if (userPaused) return;

            v.play().catch(() => {});
          }}
        onTimeUpdate={onTimeUpdate}
       onPlay={() => {
        setIsPlaying(true);
        setUserPaused(false);
        pendingPlayRef.current = false;
         }}

       onPause={() => {
         setIsPlaying(false);
         }}

       onEnded={() => {
  const v = videoRef.current;
  if (!v) return;

  pendingPlayRef.current = false;
  v.currentTime = 0;
  setProgress(0);
  setIsPlaying(false);
  setUserPaused(true);
}}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          display: 'block',
          transform: 'translateZ(0)',
          backfaceVisibility: 'hidden',
          ...style,
        }}
      />

      {showControls && (
        <div
          style={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            zIndex: 5,
            width: '100%',
            background: 'rgba(0,0,0,0.65)',
            backdropFilter: 'blur(6px)',
            borderBottomLeftRadius: 12,
            borderBottomRightRadius: 12,
            overflow: 'hidden',
          }}
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              padding: '5px 8px',
              gap: 8,
            }}
          >
            <button
  onPointerDown={(e) => {
    e.stopPropagation();
    togglePlay(e);
  }}
  style={controlBtn}
>
              {isPlaying ? <PauseIcon /> : <PlayIcon />}
            </button>

            <span
              style={{
                fontSize: 11,
                color: '#bbb',
                whiteSpace: 'nowrap',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {formatTime(videoRef.current?.currentTime || 0)}/
              {formatTime(videoRef.current?.duration || 0)}
            </span>

            <div style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
              <input
                type="range"
                min={0}
                max={100}
                value={progress}
                onPointerDown={(e) => e.stopPropagation()}
                onChange={(e) => handleScrub(Number(e.target.value))}
                style={{
                  width: '100%',
                  height: 3,
                  background: `linear-gradient(
                    to right,
                    #d8cece 0%,
                    #d8cece ${progress}%,
                    #555 ${progress}%,
                    #555 100%
                  )`,
                }}
              />
            </div>

            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsMuted((prev) => !prev);
              }}
              style={controlBtnVolume}
            >
              {isMuted ? <SpeakerOffIcon /> : <SpeakerOnIcon />}
            </button>

            <input
              type="range"
              min={0}
              max={1}
              step={0.05}
              value={isMuted ? 0 : volume}
              onPointerDown={(e) => e.stopPropagation()}
              onChange={(e) => {
                const val = Number(e.target.value);
                setIsMuted(val === 0);
                handleVolume(val);
              }}
              style={{
                width: 40,
                height: 2,
                background: `linear-gradient(
                  to right,
                  #bcb3b3 0%,
                  #d8cece ${(isMuted ? 0 : volume) * 100}%,
                  #666 ${(isMuted ? 0 : volume) * 100}%,
                  #666 100%
                )`,
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
};

const controlBtn: React.CSSProperties = {
  background: 'transparent',
  border: 'none',
  color: '#bcb4b4',
  borderRadius: 6,
  cursor: 'pointer',
  fontSize: 20,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: 24,
  lineHeight: 1,
  padding: 0,
};

const controlBtnVolume: React.CSSProperties = {
  background: 'transparent',
  border: 'none',
  color: '#bcb4b4',
  borderRadius: 6,
  cursor: 'pointer',
  fontSize: 20,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: 24,
  lineHeight: 1,
  padding: 0,
};